/* Fleet Dispatch · NHVAS Mass Management vehicle register.
   Records tare / GVM / GCM / axle limits and scheme per truck, plus the operator's
   accreditation number, and prints a branded landscape-A4 register PDF. */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil, Printer, Scale, Save } from "lucide-react";
import { api, apiHref } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isFleet } from "@/lib/industry";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const inputCls = "w-full mt-1 rounded-none bg-transparent border border-industrial mono text-sm p-2";
const SCHEMES = ["", "GML", "CML", "HML"];
const EMPTY = { make: "", model: "", vin: "", configuration: "", tare_kg: "", gvm_kg: "", gcm_kg: "", mass_scheme: "", axle_config: "", date_entered: "", notes: "" };
const kg = (v) => { const n = Number(v); return n ? `${n.toLocaleString()} kg` : "—"; };

export default function MassManagement() {
  const { org } = useAuth();
  const [trucks, setTrucks] = useState([]);
  const [acc, setAcc] = useState("");
  const [accSaving, setAccSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(EMPTY);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/fleet/mass-management");
      setTrucks(data.trucks || []);
      setAcc(data.accreditation_no || "");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load the register"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveAcc = async () => {
    setAccSaving(true);
    try { await api.put("/fleet/mass-management/settings", { accreditation_no: acc.trim() }); toast.success("Accreditation number saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setAccSaving(false); }
  };

  const openEdit = (t) => {
    const m = t.mass_mgmt || {};
    setEditing(t);
    setF({
      make: m.make || "", model: m.model || "", vin: m.vin || "", configuration: m.configuration || "",
      tare_kg: m.tare_kg ?? "", gvm_kg: m.gvm_kg ?? "", gcm_kg: m.gcm_kg ?? "",
      mass_scheme: m.mass_scheme || "", axle_config: m.axle_config || "", date_entered: m.date_entered || "", notes: m.notes || "",
    });
    setOpen(true);
  };

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...f,
        tare_kg: f.tare_kg === "" ? null : Number(f.tare_kg),
        gvm_kg: f.gvm_kg === "" ? null : Number(f.gvm_kg),
        gcm_kg: f.gcm_kg === "" ? null : Number(f.gcm_kg),
      };
      await api.put(`/fleet/mass-management/truck/${editing.id}`, payload);
      toast.success(`${editing.number} register updated`);
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save the vehicle"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="mass-management-page">
      {!isFleet(org) ? (
        <div className="card-industrial p-10 text-center space-y-2" data-testid="mm-not-fleet">
          <Scale className="w-8 h-8 text-muted-industrial mx-auto" />
          <h1 className="heading text-xl font-black uppercase">Mass Management</h1>
          <p className="text-sm text-muted-industrial max-w-md mx-auto">The Mass Management register is available on <b>Fleet Dispatch</b> accounts only.</p>
        </div>
      ) : (
      <>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="heading text-2xl font-black uppercase flex items-center gap-2"><Scale className="w-6 h-6 text-primary-orange" />Mass Management Register</h1>
          <p className="text-sm text-muted-industrial mt-1">NHVAS Mass Management vehicle register — tare, GVM, GCM, axle limits and scheme per truck.</p>
        </div>
        <a href={apiHref("/fleet/mass-management/register.pdf")} target="_blank" rel="noreferrer" data-testid="mm-print-pdf"
          className="inline-flex items-center gap-2 bg-primary-orange text-white px-4 py-2 rounded-none text-sm font-bold uppercase tracking-wide hover:opacity-90 transition-opacity">
          <Printer className="w-4 h-4" /> Export / Print PDF
        </a>
      </div>

      <div className="card-industrial p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[16rem]">
          <span className="label-tag text-[9px]">NHVAS Mass Management Accreditation No.</span>
          <input data-testid="mm-accreditation-input" value={acc} onChange={(e) => setAcc(e.target.value)} placeholder="e.g. 12345" className={inputCls} />
        </div>
        <button data-testid="mm-accreditation-save" onClick={saveAcc} disabled={accSaving}
          className="inline-flex items-center gap-2 border border-industrial px-4 py-2 rounded-none text-sm uppercase tracking-wide hover:bg-surface-2 disabled:opacity-50">
          {accSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </button>
      </div>

      <div className="card-industrial overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-muted-industrial"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> loading…</div>
        ) : trucks.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-industrial">No trucks yet. Add trucks in the Fleet page, then record their mass details here.</div>
        ) : (
          <table className="w-full text-left text-sm" data-testid="mm-table">
            <thead className="bg-surface-2">
              <tr className="mono text-[9px] uppercase tracking-widest text-muted-industrial">
                <th className="px-3 py-2">Unit / Rego</th><th className="px-3 py-2">Make / Model</th><th className="px-3 py-2">VIN / Chassis</th>
                <th className="px-3 py-2">Config</th><th className="px-3 py-2">Tare</th><th className="px-3 py-2">GVM</th><th className="px-3 py-2">GCM</th>
                <th className="px-3 py-2">Scheme</th><th className="px-3 py-2">Axle limits</th><th className="px-3 py-2">Entered</th><th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {trucks.map((t) => {
                const m = t.mass_mgmt || {};
                return (
                  <tr key={t.id} className="border-t border-industrial/60" data-testid={`mm-row-${t.number}`}>
                    <td className="px-3 py-2 mono"><div className="font-bold text-ink">{t.number}</div><div className="text-[11px] text-muted-industrial">{(t.rego || "no rego").toUpperCase()}</div></td>
                    <td className="px-3 py-2">{m.make ? <><div className="text-ink">{m.make}</div><div className="text-[11px] text-muted-industrial">{m.model}</div></> : <span className="text-muted-industrial">—</span>}</td>
                    <td className="px-3 py-2 mono text-[11px] text-muted-industrial">{(m.vin || "—").toUpperCase()}</td>
                    <td className="px-3 py-2 text-[12px]">{m.configuration || "—"}</td>
                    <td className="px-3 py-2 mono text-[12px]">{kg(m.tare_kg)}</td>
                    <td className="px-3 py-2 mono text-[12px]">{kg(m.gvm_kg)}</td>
                    <td className="px-3 py-2 mono text-[12px]">{kg(m.gcm_kg)}</td>
                    <td className="px-3 py-2 mono text-[11px] font-bold text-primary-orange">{m.mass_scheme || "—"}</td>
                    <td className="px-3 py-2 text-[11px] text-muted-industrial">{m.axle_config || "—"}</td>
                    <td className="px-3 py-2 mono text-[11px] text-muted-industrial">{m.date_entered || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => openEdit(t)} data-testid={`mm-edit-${t.number}`} className="inline-flex items-center gap-1 text-xs text-primary-orange hover:underline"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) setOpen(false); }}>
        <DialogContent className="max-w-2xl bg-surface border-industrial" data-testid="mm-edit-dialog">
          <DialogHeader>
            <DialogTitle className="heading uppercase font-black">Mass details · {editing?.number}</DialogTitle>
            <DialogDescription className="text-sm text-muted-industrial">Recorded mass-management values for this vehicle. These appear on the printed register.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-muted-industrial">Make<input data-testid="mm-f-make" value={f.make} onChange={set("make")} className={inputCls} /></label>
            <label className="text-xs text-muted-industrial">Model<input value={f.model} onChange={set("model")} className={inputCls} /></label>
            <label className="text-xs text-muted-industrial col-span-2">VIN / Chassis no.<input data-testid="mm-f-vin" value={f.vin} onChange={set("vin")} className={inputCls} /></label>
            <label className="text-xs text-muted-industrial">Configuration<input value={f.configuration} onChange={set("configuration")} placeholder="e.g. truck & dog, semi" className={inputCls} /></label>
            <label className="text-xs text-muted-industrial">Mass limit scheme
              <select data-testid="mm-f-scheme" value={f.mass_scheme} onChange={set("mass_scheme")} className={inputCls}>
                {SCHEMES.map((s) => <option key={s} value={s} className="bg-surface">{s || "—"}</option>)}
              </select>
            </label>
            <label className="text-xs text-muted-industrial">Tare (kg)<input data-testid="mm-f-tare" type="number" value={f.tare_kg} onChange={set("tare_kg")} className={inputCls} /></label>
            <label className="text-xs text-muted-industrial">GVM (kg)<input data-testid="mm-f-gvm" type="number" value={f.gvm_kg} onChange={set("gvm_kg")} className={inputCls} /></label>
            <label className="text-xs text-muted-industrial">GCM (kg)<input type="number" value={f.gcm_kg} onChange={set("gcm_kg")} className={inputCls} /></label>
            <label className="text-xs text-muted-industrial">Date entered register<input type="date" value={f.date_entered} onChange={set("date_entered")} className={inputCls} /></label>
            <label className="text-xs text-muted-industrial col-span-2">Axle / axle-group limits<input value={f.axle_config} onChange={set("axle_config")} placeholder="e.g. Steer 6.0t · Drive 16.5t · Trailer 22.5t" className={inputCls} /></label>
            <label className="text-xs text-muted-industrial col-span-2">Notes<input value={f.notes} onChange={set("notes")} className={inputCls} /></label>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} disabled={busy} className="border border-industrial px-4 py-2 rounded-none text-sm uppercase hover:bg-surface-2 disabled:opacity-50">Cancel</button>
            <button data-testid="mm-save" onClick={save} disabled={busy} className="bg-primary-orange text-white px-4 py-2 rounded-none text-sm font-bold uppercase hover:opacity-90 disabled:opacity-50">
              {busy ? "Saving…" : "Save vehicle"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
}
